import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Skeleton, message } from "antd";
import { useRecoilValue } from "recoil";
import { locationsState } from "../../../../state/atoms";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto, KitDeletePreviewDto } from "../../../../domain/DataKitDto";
import { locationLabel } from "./DataKitManageLocationsModal";
import "./DataKitManageLocationsModal.scss";

const COLLAPSED_ROWS = 5;

interface Props {
  kit: DataKitDto;
  onClose: () => void;
  onDeleted: () => void;
  /** "Archive it instead" - the reversible path the confirmation offers. */
  onArchiveInstead: () => void;
}

/**
 * Deleting a kit - two shapes, both from the mock.
 *
 * A kit no location uses gets a plain confirmation. A kit that locations still use gets "{kit} is
 * used by N locations" with every location and its design count (Figma 310:37411), and the button
 * says what it does: Remove and Delete. Both say the same thing about designs, because both do the
 * same thing to them - they keep today's values, unlinked, exactly as deleting an in-use item does.
 *
 * Archive is offered right there as the reversible path, the same move the item-delete
 * confirmation makes.
 */
export function DataKitDeleteKitModal({ kit, onClose, onDeleted, onArchiveInstead }: Props) {
  const locations = useRecoilValue(locationsState);
  const [preview, setPreview] = useState<KitDeletePreviewDto | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // The parent passes a fresh arrow every render; reading it through a ref keeps this fetch to once
  // per kit instead of once per render, which would also keep resetting the form under the admin.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!kit.id) return;
    DataKitApi.previewDeleteKit(kit.id)
      .then((res) => setPreview(res.data))
      .catch(() => {
        message.error("Could not check what this library is used by");
        closeRef.current();
      });
  }, [kit.id]);

  const rows = useMemo(() => {
    if (!preview) return [];
    const byId = new Map(locations.map((l) => [String(l.internalId), l]));
    return preview.locations
      .map((p) => {
        const location = byId.get(p.unitId);
        return { id: p.unitId, label: location ? locationLabel(location) : `Location ${p.unitId}`, designs: p.designCount };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [preview, locations]);

  const remove = () => {
    if (!kit.id) return;
    setDeleting(true);
    DataKitApi.deleteKit(kit.id)
      .then(onDeleted)
      .catch(() => message.error("Could not delete this library"))
      .finally(() => setDeleting(false));
  };

  const name = kit.name || "This library";
  const items = preview?.itemCount ?? kit.itemCount ?? 0;
  const itemPhrase = `${items} item${items === 1 ? "" : "s"}`;
  const inUse = rows.length > 0;
  const shown = showAll ? rows : rows.slice(0, COLLAPSED_ROWS);

  return (
    <Modal className={"clean one data-kit-manage-locations-modal"} open width={520} onCancel={onClose}>
      {!preview ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <>
          <header>
            <h2>
              {inUse
                ? `${name} is used by ${rows.length} location${rows.length === 1 ? "" : "s"}`
                : `Delete ${name}?`}
            </h2>
          </header>
          {/* The mock also said "and 4 menus". Menus belong to the account, not the kit, and
              deleting a kit leaves them alone - so that half of the sentence would have been
              false. */}
          <p className="manage-locations-sub">
            {inUse ? "This removes it from these locations and deletes its " : "This deletes its "}
            {itemPhrase}. Designs keep today&apos;s values but won&apos;t get library updates. You
            can&apos;t undo this.{" "}
            <button type="button" className="delete-kit-archive-link" onClick={onArchiveInstead}>
              Archive it instead
            </button>
          </p>

          {inUse && (
            <>
              <ul className="manage-locations-list">
                {shown.map((row) => (
                  <li key={row.id} className="delete-kit-location">
                    <span>{row.label}</span>
                    <span className="delete-kit-designs">
                      {row.designs} design{row.designs === 1 ? "" : "s"}
                    </span>
                  </li>
                ))}
              </ul>
              {rows.length > shown.length && (
                <button type="button" className="manage-locations-more" onClick={() => setShowAll(true)}>
                  + Show {rows.length - shown.length} more
                </button>
              )}
            </>
          )}
        </>
      )}

      <footer>
        <Button size="large" onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button size="large" className="primary" loading={deleting} disabled={!preview} onClick={remove}>
          {inUse ? "Remove and Delete" : "Delete"}
        </Button>
      </footer>
    </Modal>
  );
}
